import React, { FC, useEffect, useState } from "react";
import { MarkerData } from "./types";
import {
  MapObjectCard,
  ObjectMetadataTags,
  utils
} from "@vilocnv/allsetra-core";
import {
  Skeleton,
  SkeletonWrapper
} from "components/maps/common/ObjectMarker.styled";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button as MuiButton, Stack } from "@mui/material";

import { isEmpty } from "lodash";
import {
  useAppDispatch,
  useAppSelector,
  useMarkerResolvedAddress
} from "hooks";
import {
  selectAccountFeaturesState,
  selectSpecificObjectState
} from "app/data/selectors";
import { setActiveObjectId } from "app/features";
import { LinkArrowIcon } from "assets/icons";
import { MapObjectCardBox } from "../../Map.styled";
import {
  getAddressDisplayInfo,
  getResolvedAddressFromStore,
  isInactiveObject
} from "app/data/helpers";
import InactiveObjectBadge from "components/ui/InactiveObjectBadge";
import { useMapState } from "./MapStateContext";

interface MarkerInfoWindowProps {
  marker: MarkerData;
  showLinkIcon?: boolean;
}

const MarkerInfoWindowContent: FC<MarkerInfoWindowProps> = ({
  marker,
  showLinkIcon
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [address, setAddress] = useState<string | null>(null);
  const { enableLastTripsAction, onOpenLastTrips, activeObjectId } =
    useMapState();
  const { features } = useAppSelector(selectAccountFeaturesState);

  const { markerLoading, selectedMarker } = useAppSelector(
    selectSpecificObjectState
  );

  const showLastTripsAction =
    enableLastTripsAction &&
    features.isRidesEnabled &&
    Boolean(marker?.uniqueId) &&
    Boolean(onOpenLastTrips) &&
    activeObjectId === marker.uniqueId;

  const { resolvedAddresses, markerAddressLoading } =
    useMarkerResolvedAddress(selectedMarker);

  const latitude = selectedMarker?.location?.latitude;
  const longitude = selectedMarker?.location?.longitude;
  const objectResolvedAddress = selectedMarker?.location?.resolvedAddress;

  const finalResolvedAddress = getResolvedAddressFromStore(
    objectResolvedAddress,
    resolvedAddresses || [],
    latitude,
    longitude
  );

  const addressInfo = getAddressDisplayInfo(
    latitude,
    longitude,
    finalResolvedAddress
  );

  const inactive =
    marker.isInactive ||
    isInactiveObject(marker.status ?? selectedMarker?.status);

  useEffect(() => {
    setAddress(finalResolvedAddress || null);

    return () => {
      setAddress(null);
    };
  }, [finalResolvedAddress]);

  return (
    <MapObjectCardBox>
      <MapObjectCard
        label={marker?.name || t("typography.na")}
        id={marker.aNumber || t("typography.na")}
        loading={false}
        location={{
          isLoading: markerLoading || markerAddressLoading,
          address: address ?? "",
          latitude: selectedMarker?.location?.latitude,
          longitude: selectedMarker?.location?.longitude,
          addressInfo
        }}
        accuracy={marker?.accuracy}
        time={
          markerLoading
            ? "..."
            : utils.formatDate({
                date: selectedMarker?.location?.date
              })
        }
        handleObjectNameClick={() =>
          navigate({
            pathname: `/dashboard/objects/details/${marker.uniqueId}`
          })
        }
        translator={t}
        showLinkIcon={showLinkIcon}
        linkIcon={showLinkIcon ? <LinkArrowIcon /> : <></>}
        actions={
          showLastTripsAction ? (
            <MuiButton
              variant="outlined"
              size="small"
              fullWidth
              onClick={(event) => {
                event.stopPropagation();
                if (!marker.uniqueId) return;
                dispatch(setActiveObjectId(marker.uniqueId));
                onOpenLastTrips?.(marker.uniqueId, marker.name);
              }}
              sx={{
                color: "#FFFFFF",
                borderColor: "rgba(255, 255, 255, 0.6)",
                "&:hover": {
                  borderColor: "#FFFFFF",
                  backgroundColor: "rgba(255, 255, 255, 0.08)"
                }
              }}
            >
              {t("common.lastTrips")}
            </MuiButton>
          ) : undefined
        }
      >
        {inactive ? (
          <Stack mb={1}>
            <InactiveObjectBadge variant="inline" />
          </Stack>
        ) : null}
        {!isEmpty(selectedMarker) && !markerLoading ? (
          <ObjectMetadataTags
            translator={t}
            object={selectedMarker}
            direction={"row"}
          />
        ) : (
          <SkeletonWrapper>
            <Skeleton variant="rectangular" />
          </SkeletonWrapper>
        )}
      </MapObjectCard>
    </MapObjectCardBox>
  );
};

export default React.memo(MarkerInfoWindowContent);
